import { RecoverableNetworkError, retryWithBackoff } from '@canvas/retry';

import { AtlassianClientFetchError } from './atlassian-client-error';
import { type ExtendedFetcher } from './atlassian-client-types';
import { StaleWhileRevalidateCache } from './strategies/stale-while-revalidate';

type CacheStrategy = 'none' | 'stale-while-revalidate';
type CacheOpts = {
  strategy: CacheStrategy;
  lifetime?: number;
};

export type RequestMetadata = {
  requestType: string;
};

const getJsonResponse = async (
  result: Response,
  metadata?: RequestMetadata,
): Promise<any> => {
  if (result.status >= 500) {
    throw new AtlassianClientFetchError(
      result.status,
      result.statusText,
      await result.text(),
      undefined,
      metadata,
    );
  }
  try {
    return await result.clone().json();
  } catch {
    throw new AtlassianClientFetchError(
      result.status,
      result.statusText,
      await result.text(),
      undefined,
      metadata,
    );
  }
};

export class AtlassianClient {
  private staleWhileRevalidateCache = new StaleWhileRevalidateCache();

  public constructor(
    private fetcher: ExtendedFetcher,
    private baseUrl?: URL,
  ) {}

  public get base() {
    return this.baseUrl;
  }

  public async get<ResponseType>(
    url: string,
    queryParams?: Record<string, string>,
    cachingOpts: CacheOpts = { strategy: 'none' },
    metadata?: RequestMetadata,
  ): Promise<ResponseType> {
    const endpoint = this.getEndpoint(url);
    if (queryParams) {
      for (const [key, value] of Object.entries(queryParams)) {
        endpoint.searchParams.set(key, value);
      }
    }

    if (cachingOpts.strategy === 'stale-while-revalidate') {
      const opts = cachingOpts.lifetime ? { lifetime: cachingOpts.lifetime } : undefined;
      const boundExecutor = this.executeGet.bind(this);
      const executor = <ResponseType>(url: URL) =>
        boundExecutor<ResponseType>(url, undefined, metadata);

      return this.staleWhileRevalidateCache.get(endpoint, executor, opts);
    }

    return await this.executeGet<ResponseType>(endpoint, undefined, metadata);
  }

  private async executeGet<ResponseType>(
    endpoint: URL,
    headers: HeadersInit = {},
    metadata?: RequestMetadata,
  ): Promise<ResponseType> {
    const result = await this.resilientFetch(endpoint.href, {
      method: 'GET',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        ...headers,
      },
      metadata,
    });

    return await getJsonResponse(result, metadata);
  }

  private getEndpoint(url: string) {
    if (!this.baseUrl) {
      return new URL(url);
    }

    if (this.baseUrl.pathname !== '/') {
      return new URL(this.baseUrl.pathname + url, this.baseUrl);
    }

    return new URL(url, this.baseUrl);
  }

  public async post<RequestType extends Record<string, unknown>, ResponseType>(
    url: string,
    params: RequestType,
    headers: HeadersInit = {},
    metadata?: RequestMetadata,
    signal?: AbortSignal,
  ): Promise<ResponseType> {
    const endpoint = this.getEndpoint(url);
    const result = await this.resilientFetch(endpoint.href, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(params),
      headers: {
        'content-type': 'application/json',
        ...headers,
      },
      metadata,
      signal,
    });

    return await getJsonResponse(result);
  }

  public async postWithoutResponse<RequestType extends Record<string, unknown>>(
    url: string,
    params: RequestType,
    headers: HeadersInit = {},
    metadata?: RequestMetadata,
  ): Promise<void> {
    const endpoint = this.getEndpoint(url);
    await this.resilientFetch(endpoint.href, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(params),
      headers: {
        'content-type': 'application/json',
        ...headers,
      },
      metadata,
    });
  }

  public async put<RequestType extends Record<string, unknown>, ResponseType>(
    url: string,
    params: RequestType,
    metadata?: RequestMetadata,
  ): Promise<ResponseType> {
    const endpoint = this.getEndpoint(url);
    const result = await this.resilientFetch(endpoint.href, {
      method: 'PUT',
      credentials: 'include',
      body: JSON.stringify(params),
      headers: {
        'content-type': 'application/json',
      },
      metadata,
    });

    // no content
    if (result.status === 204) {
      return {} as ResponseType;
    }

    const json = await getJsonResponse(result);

    return { ...json, errorStatusCode: result.status };
  }

  public async delete(url: string, metadata?: RequestMetadata): Promise<void> {
    const endpoint = this.getEndpoint(url);
    await this.resilientFetch(endpoint.href, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
      },
      metadata,
    });
  }

  private resilientFetch(...args: Parameters<ExtendedFetcher>) {
    return retryWithBackoff({
      callback: async () => {
        const response = await this.fetcher(...args);

        if (response.status > 500) {
          throw new RecoverableNetworkError('transient network failure');
        }

        return response;
      },
      maxRetries: 5,
      retryTimeMs: process.env.NODE_ENV === 'test' ? 10 : undefined,
    });
  }
}
